import React, { useEffect, useState } from 'react';
import PageBanner from '../ui/PageBanner';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { MapPin, Mail, Phone, CheckCircle } from 'lucide-react';
import contactBgImage from '../assets/Contact/contact-bg.png';
import contactUsBannerImage from '../assets/Contact/ContactUsBanner.jpg';
import Preloader from '../ui/Preloader';
import emailjs from 'emailjs-com';

const Contact = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    AOS.init({ duration: 1000, once: false });
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(delay);
  }, []);

  const validate = () => {
    const newErrors = {};
    if (formData.name.trim().length < 3) newErrors.name = 'Username should be at least 3 characters long';
    if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Enter valid email id';
    if (formData.subject.trim().length < 5) newErrors.subject = 'Subject should be at least 5 characters long';
    if (formData.message.trim().length < 10) newErrors.message = 'Message should be at least 10 characters long';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    setError('');
    if (Object.keys(validationErrors).length === 0) {
      setSubmitting(true);
      emailjs
        .send(
          process.env.REACT_APP_EMAIL_JS_SERVICE_ID,
          process.env.REACT_APP_EMAIL_JS_TEMPLATE_ID,
          {
            name: formData.name,
            email: formData.email,
            subject: formData.subject,
            message: formData.message,
          },
          process.env.REACT_APP_EMAIL_JS_API_KEY
        )
        .then(() => {
          setSuccess(true);
          setFormData({ name: '', email: '', subject: '', message: '' });
          setTimeout(() => setSuccess(false), 4000);
        })
        .catch(() => {
          setError('Something went wrong while sending your message. Please try again later.');
        })
        .finally(() => setSubmitting(false));
    }
  };

  return (
    <>
      {loading ? (
        <Preloader />
      ) : (
        <div className="bg-white">
          <PageBanner
            title="Contact Us"
            breadcrumb="Contact Us"
            backgroundImage={contactUsBannerImage}
          />
          <div className="relative -mt-12">
            <div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `url(${contactBgImage})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top center',
                opacity: 0.6,
                filter: 'grayscale(60%) contrast(105%) brightness(110%)',
              }}
            ></div>
            <div className="relative z-10">
              <div className="text-center mt-10 pt-4 md:pt-20 px-4" data-aos="fade-up">
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">
                  Need Help? Let’s Get in Touch
                </h2>
                <div className="w-12 h-0.5 bg-[#2584C6] mx-auto mt-2 rounded"></div>
              </div>
              <div className="md:flex justify-evenly gap-6 p-6 md:p-12 max-w-screen">
                <div className="space-y-6" data-aos="fade-right">
                  <div>
                    <h3 className="text-xl font-semibold text-[#F7941D] flex items-center gap-2 mb-2">
                      <MapPin className="text-[#F7941D]" /> Location:
                    </h3>
                    <p className="ml-6 text-[#2584C6]">
                      <strong>India:</strong><br />
                      Level 4, Krishe Emrald, Survey No. 11,<br />
                      Kondapur Main Road, Laxmi Cyber City,<br />
                      Whitefields, Kondapur, Hyderabad – 500081.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#F7941D] flex items-center gap-2 mb-2">
                      <Mail className="text-[#F7941D]" /> Email:
                    </h3>
                    <p className="ml-6 text-[#2584C6] font-medium">
                      <a href="mailto:info@esmobtechnologies.com" className="hover:underline">
                        info@esmobtechnologies.com
                      </a>
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#F7941D] flex items-center gap-2 mb-2">
                      <Phone className="text-[#F7941D]" /> Call:
                    </h3>
                    <p className="ml-6 text-gray-700">+91 40 – 40358 20055</p>
                  </div>
                </div>
                <div className="p-6 rounded-lg" data-aos="fade-left">
                  {success && (
                    <div className="flex items-center gap-2 bg-green-100 border border-green-400 text-green-800 px-4 py-3 rounded mb-4">
                      <CheckCircle className="text-green-600" size={20} />
                      <span>Message sent successfully!</span>
                    </div>
                  )}
                  {error && (
                    <div className="flex items-center gap-2 bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded mb-4">
                      <span>{error}</span>
                    </div>
                  )}
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Your Name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className={`w-full border ${errors.name ? 'border-red-500' : 'border-gray-300'} p-3 rounded focus:outline-none focus:ring-2 focus:ring-[#2584C6]`}
                        />
                        {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                      </div>
                      <div>
                        <input
                          type="email"
                          placeholder="E-mail"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className={`w-full border ${errors.email ? 'border-red-500' : 'border-gray-300'} p-3 rounded focus:outline-none focus:ring-2 focus:ring-[#2584C6]`}
                        />
                        {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
                      </div>
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className={`w-full border ${errors.subject ? 'border-red-500' : 'border-gray-300'} p-3 rounded focus:outline-none focus:ring-2 focus:ring-[#2584C6]`}
                      />
                      {errors.subject && <p className="text-sm text-red-600 mt-1">{errors.subject}</p>}
                    </div>
                    <div>
                      <textarea
                        rows="5"
                        placeholder="Message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className={`w-full border ${errors.message ? 'border-red-500' : 'border-gray-300'} p-3 rounded focus:outline-none focus:ring-2 focus:ring-[#2584C6]`}
                      ></textarea>
                      {errors.message && <p className="text-sm text-red-600 mt-1">{errors.message}</p>}
                    </div>
                    <div className="text-center">
                      <button
                        type="submit"
                        disabled={submitting}
                        className={`bg-[#F7941D] hover:bg-orange-500 text-white px-6 py-2 rounded uppercase tracking-wide font-semibold transition duration-200 ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {submitting ? 'Sending...' : 'Submit'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Contact;
